import { Link } from "react-router-dom";
import { ArrowRight, HandHeart, PiggyBank, PawPrint, Dog, Cat, Rabbit } from "lucide-react";
import { HouseHeart } from "@/components/icons/HouseHeart";
import { trackEvent } from "@/hooks/useEventTracking";
import { shelterFields } from "@/lib/analyticsEvents";
import { supabase } from "@/integrations/supabase/client";
import { shelterUrl } from "@/lib/shelterSlug";
import { isUrgentNeedsActive, type Shelter } from "@/types/shelter";
import type { ShelterSectionsBundle } from "@/types/shelterSections";
import { isSectionUrgentActive } from "@/types/shelterSections";

const AdoptionPlaceholderIcon = ({ species, className }: { species?: string | null; className?: string }) => {
  const Icon = species === "kot" ? Cat : species === "inne" ? Rabbit : Dog;
  return <Icon className={className} strokeWidth={1.5} />;
};

// Kafelki: Potrzeby / Zbiorka / Adopcja. Pokazujemy tylko te, ktore maja tresc.
// Pilnosc = gruba pomaranczowa ramka + badge "Pilne".

interface ShelterCardSectionsProps {
  shelter: Shelter;
  sections: ShelterSectionsBundle;
}

type SectionKey = "needs" | "fundraiser" | "adoption";

const SECTION_STYLES: Record<SectionKey, {
  iconBg: string;
  iconColor: string;
  title: string;
  cta: string;
  bullet: string;
  divider: string;
  base: string;
  urgent: string;
  urgentBadge: string;
}> = {
  needs: {
    iconBg: "bg-section-needs-bg",
    iconColor: "text-section-needs",
    title: "text-section-needs",
    cta: "text-section-needs",
    bullet: "bg-section-needs",
    divider: "border-section-needs/10",
    base: "bg-card ring-1 ring-inset ring-section-needs/15 hover:ring-section-needs/30 hover:shadow-[0_8px_24px_-6px_hsl(var(--section-needs)/0.15)]",
    urgent: "bg-section-needs-bg/30 ring-1 ring-inset ring-section-needs/30 shadow-[0_4px_16px_-6px_hsl(var(--section-needs)/0.18)]",
    urgentBadge: "bg-section-needs text-white",
  },
  fundraiser: {
    iconBg: "bg-section-fundraiser-bg",
    iconColor: "text-section-fundraiser",
    title: "text-section-fundraiser-foreground",
    cta: "text-section-fundraiser-foreground",
    bullet: "bg-section-fundraiser",
    divider: "border-section-fundraiser/10",
    base: "bg-card ring-1 ring-inset ring-section-fundraiser/15 hover:ring-section-fundraiser/30 hover:shadow-[0_8px_24px_-6px_hsl(var(--section-fundraiser)/0.15)]",
    urgent: "bg-section-fundraiser-bg/40 ring-1 ring-inset ring-section-fundraiser/30 shadow-[0_4px_16px_-6px_hsl(var(--section-fundraiser)/0.18)]",
    urgentBadge: "bg-section-fundraiser text-white",
  },
  adoption: {
    iconBg: "bg-section-adoption-bg",
    iconColor: "text-section-adoption",
    title: "text-section-adoption-foreground",
    cta: "text-section-adoption-foreground",
    bullet: "bg-section-adoption",
    divider: "border-section-adoption/10",
    base: "bg-card ring-1 ring-inset ring-section-adoption/15 hover:ring-section-adoption/30 hover:shadow-[0_8px_24px_-6px_hsl(var(--section-adoption)/0.15)]",
    urgent: "bg-section-adoption-bg/30 ring-1 ring-inset ring-section-adoption/30 shadow-[0_4px_16px_-6px_hsl(var(--section-adoption)/0.18)]",
    urgentBadge: "bg-section-adoption text-white",
  },
};

const SPECIES_LABEL: Record<string, string> = {
  pies: "Pies",
  kot: "Kot",
  inne: "Inne",
};

const adoptionPhotoUrl = (path: string | null): string | null => {
  if (!path) return null;
  const { data } = supabase.storage.from("adoption-photos").getPublicUrl(path);
  return data.publicUrl;
};

const TileShell = ({
  styleKey,
  urgent,
  icon: Icon,
  title,
  body,
  cta,
  rightSlot,
  onClick,
  href,
  external,
}: {
  styleKey: SectionKey;
  urgent: boolean;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  body: React.ReactNode;
  cta: string;
  rightSlot?: React.ReactNode;
  onClick?: (e: React.MouseEvent) => void;
  href: string;
  external?: boolean;
}) => {
  const s = SECTION_STYLES[styleKey];
  const className = `group/tile relative flex flex-col rounded-xl p-2.5 transition-all duration-300 ease-out hover:-translate-y-0.5 ${urgent ? s.urgent : s.base}`;

  const content = (
    <>
      {urgent && (
        <span className={`absolute top-2 right-2 inline-flex items-center px-1.5 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-[0.08em] leading-none shadow-sm ${s.urgentBadge}`}>
          Pilne
        </span>
      )}
      <div className="flex items-center gap-1.5 mb-1.5">
        <div className={`flex-shrink-0 w-6 h-6 rounded-lg ${s.iconBg} flex items-center justify-center`}>
          <Icon className={`w-3 h-3 ${s.iconColor}`} />
        </div>
        <p className={`text-[11.5px] sm:text-[12.5px] font-bold tracking-[0.01em] leading-none ${s.title}`}>
          {title}
        </p>
      </div>
      <div className="flex gap-2 flex-1">
        <div className="min-w-0 flex-1 text-[11px] sm:text-[11.5px] leading-snug text-foreground/85">
          {body}
        </div>
        {rightSlot}
      </div>
      <div className="mt-1.5">
        <span className={`inline-flex items-center gap-1 text-[11.5px] sm:text-[12px] font-semibold ${s.cta}`}>
          <span className="relative">
            {cta}
            <span className="absolute left-0 -bottom-px h-px w-0 bg-current transition-all duration-300 group-hover/tile:w-full" />
          </span>
          <ArrowRight className="w-3 h-3 transition-transform duration-300 group-hover/tile:translate-x-0.5" />
        </span>
      </div>
    </>
  );

  if (external) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" onClick={onClick} className={className}>
        {content}
      </a>
    );
  }
  return (
    <Link to={href} onClick={onClick} className={className}>
      {content}
    </Link>
  );
};

export const ShelterCardSections = ({ shelter, sections }: ShelterCardSectionsProps) => {
  const { urgentNeeds, fundraiser, adoption } = sections;

  // "Pilne" wymaga obu warunkow: flaga shelter.urgent_needs (z 30-dniowym TTL)
  // ORAZ wpisanych pozycji w shelter_urgent_needs. Wylaczenie flagi w panelu
  // ukrywa wyroznienie i bullet liste - karta wraca do zwyklego current_needs.
  const showUrgentNeeds = isUrgentNeedsActive(shelter) && urgentNeeds.length > 0;
  const needsUrgent = showUrgentNeeds;
  const fundUrgent = !!fundraiser && isSectionUrgentActive(fundraiser.is_urgent, fundraiser.urgent_set_at);
  const adoptUrgent = !!adoption && isSectionUrgentActive(adoption.is_urgent, adoption.urgent_set_at);

  const profileHref = shelterUrl(shelter);

  const needsItems: string[] = showUrgentNeeds
    ? urgentNeeds.slice(0, 4).map((n) => n.text)
    : shelter.current_needs
      ? shelter.current_needs
          .split(/[\n;]+/)
          .map((s) => s.trim())
          .filter(Boolean)
          .slice(0, 4)
      : [];
  const hasNeeds = needsItems.length > 0;

  const adoptionPhoto = adoption ? adoptionPhotoUrl(adoption.photo_path) : null;

  const hasAny = hasNeeds || !!fundraiser || !!adoption;
  if (!hasAny) return null;

  // Liczba aktywnych kafelkow -> dobierz grid
  const activeCount = (hasNeeds ? 1 : 0) + (fundraiser ? 1 : 0) + (adoption ? 1 : 0);
  const gridCols = activeCount === 1
    ? "grid-cols-1"
    : activeCount === 2
      ? "grid-cols-1 sm:grid-cols-2"
      : "grid-cols-1 sm:grid-cols-3";

  return (
    <div className="mt-3">
      <div className={`grid ${gridCols} gap-2 sm:gap-2.5`}>
        {hasNeeds && (
          <TileShell
            styleKey="needs"
            urgent={needsUrgent}
            icon={HandHeart}
            title="Potrzeby"
            cta="Zobacz potrzeby"
            href={profileHref}
            onClick={(e) => {
              e.stopPropagation();
              trackEvent("Karta - kafelek potrzeb", { ...shelterFields(shelter), urgent: needsUrgent });
            }}
            body={
              needsUrgent ? (
                <ul className="space-y-1">
                  {needsItems.map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500" />
                      <span className="line-clamp-3">{item}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="line-clamp-4">

                  {needsItems.join(", ")}
                  {needsItems.length > 0 && !/[.!?]$/.test(needsItems[needsItems.length - 1]) ? "." : ""}
                </p>
              )
            }
          />
        )}

        {fundraiser && (
          <TileShell
            styleKey="fundraiser"
            urgent={fundUrgent}
            icon={PiggyBank}
            title="Zbiórka"
            cta="Wesprzyj zbiórkę"
            href={fundraiser.external_url}
            external
            onClick={(e) => {
              e.stopPropagation();
              trackEvent("Karta - wesprzyj zbiorke", {
                ...shelterFields(shelter),
                fundraiser_url: fundraiser.external_url,
                urgent: fundUrgent,
              });
            }}
            body={
              <div className="space-y-1.5">
                <p className="line-clamp-2">{fundraiser.title}</p>
                <FundraiserStats fundraiser={fundraiser} />
              </div>
            }
          />
        )}

        {adoption && (
          <TileShell
            styleKey="adoption"
            urgent={adoptUrgent}
            icon={HouseHeart}
            title="Adopcja"
            cta={`Poznaj - ${adoption.name.split(/[\s-]/)[0]}`}
            href={profileHref}
            onClick={(e) => {
              e.stopPropagation();
              trackEvent("Karta - poznaj zwierzaka", {
                ...shelterFields(shelter),
                adoption_id: adoption.id,
                urgent: adoptUrgent,
              });
            }}
            rightSlot={
              adoptionPhoto ? (
                <img
                  src={adoptionPhoto}
                  alt={`Zdjęcie ${adoption.name}`}
                  loading="lazy"
                  className="flex-shrink-0 w-14 h-14 rounded-full object-cover self-center"
                />
              ) : (
                <div className="flex-shrink-0 w-14 h-14 rounded-full bg-section-adoption-bg flex items-center justify-center self-center">
                  <AdoptionPlaceholderIcon species={adoption.species} className="w-7 h-7 text-section-adoption" />
                </div>
              )
            }
            body={
              <div className="space-y-1">
                <p className="font-medium text-card-foreground line-clamp-1">
                  {adoption.name}
                  {adoption.species && ` - ${SPECIES_LABEL[adoption.species] ?? "Inne"}`}
                  {adoption.age && ` - ${adoption.age}`}
                </p>
                {adoption.description && (
                  <p className="line-clamp-3">{adoption.description}</p>
                )}
              </div>
            }
          />
        )}
      </div>
    </div>
  );
};

// ---- Fundraiser stats: pasek postepu + kwota + procent + "Pobrano X temu" ----

const formatPLN = (n: number): string => {
  // Kompaktowo: < 1000 -> "850 zł", < 1 mln -> "12,3 tys. zł" / "120 tys. zł",
  // >= 1 mln -> "1,2 mln zł". Mniej cyfr = czytelniej na karcie.
  const abs = Math.abs(n);
  if (abs >= 1_000_000) {
    const v = n / 1_000_000;
    const str = (Math.round(v * 10) / 10).toLocaleString("pl-PL", { maximumFractionDigits: 1 });
    return `${str} mln zł`;
  }
  if (abs >= 10_000) {
    const v = Math.round(n / 1000);
    return `${v.toLocaleString("pl-PL").replace(/\u00A0/g, " ")} tys. zł`;
  }
  if (abs >= 1000) {
    const v = Math.round((n / 1000) * 10) / 10;
    const str = v.toLocaleString("pl-PL", { maximumFractionDigits: 1 });
    return `${str} tys. zł`;
  }
  return `${Math.round(n).toLocaleString("pl-PL").replace(/\u00A0/g, " ")} zł`;
};


const formatRelative = (iso: string): string => {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.max(0, Math.round(diffMs / 60000));
  if (mins < 60) return mins <= 1 ? "przed chwilą" : `${mins} min temu`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return hours === 1 ? "1 godz. temu" : `${hours} godz. temu`;
  const days = Math.round(hours / 24);
  if (days === 1) return "wczoraj";
  if (days < 7) return `${days} dni temu`;
  const weeks = Math.round(days / 7);
  if (weeks === 1) return "tydzień temu";
  return `${weeks} tyg. temu`;
};

interface FundraiserStatsProps {
  fundraiser: {
    amount_raised: number | null;
    amount_goal: number | null;
    currency: string | null;
    stats_updated_at: string | null;
  };
}

const FundraiserStats = ({ fundraiser }: FundraiserStatsProps) => {
  const { amount_raised, amount_goal, stats_updated_at } = fundraiser;
  if (amount_raised == null && amount_goal == null) return null;

  const raised = amount_raised ?? 0;
  const goal = amount_goal ?? null;
  const pct = goal && goal > 0 ? Math.min(100, Math.round((raised / goal) * 100)) : null;

  return (
    <div className="space-y-1 pt-0.5">
      {goal != null && (
        <div className="h-2 w-full rounded-full bg-section-fundraiser/15 overflow-hidden">
          <div
            className="h-full rounded-full bg-section-fundraiser transition-[width] duration-500"
            style={{ width: `${pct ?? 0}%` }}
          />
        </div>
      )}
      <p className="text-[11px] sm:text-[11.5px] leading-tight">
        <span className="font-bold text-section-fundraiser">{formatPLN(raised)}</span>
        {goal != null && (
          <>
            <span className="text-muted-foreground"> / {formatPLN(goal)}</span>
            {pct != null && <span className="ml-1 text-[10px] text-muted-foreground/60">· {pct}%</span>}
          </>
        )}
      </p>

      {stats_updated_at && (
        <p className="text-[10px] text-muted-foreground/80 leading-tight">
          Stan na {formatRelative(stats_updated_at)}
        </p>
      )}
    </div>
  );
};

